export type PageIdentity = {
  pageType: string;
  pageId: string;
};

/**
 * URL segment patterns (after siteBasePath has been stripped):
 *   /{floor}/category/...?cid=123  → category page: 123
 *   /search?q=...                  → successful search (corrected to failed search by ProductListingView when itemCount=0)
 *   /product/.../12345             → product page: 12345
 *   /wishlist                      → saved items
 *   /bag                           → shopping bag
 *
 * After stripping siteBasePath, empty segments = homepage:
 *   /gb → [] → home page
 *
 * Pages router floor pages:
 *   /gb/topshop → ["topshop"] → floor page: women
 *   /gb/topman  → ["topman"]  → floor page: men
 */

const SEGMENT_PATTERNS: {
  match: (segments: string[]) => boolean;
  resolve: (segments: string[], searchParams: URLSearchParams) => PageIdentity;
}[] = [
  {
    match: (segments) => segments.includes("category"),
    resolve: (_segments, searchParams) => {
      const cid = searchParams.get("cid") ?? "";
      return {
        pageType: "category page",
        pageId: `category page: ${cid}`,
      };
    },
  },
  {
    match: (segments) => segments.includes("product"),
    resolve: (segments) => {
      const productId = segments[segments.length - 1] ?? "";
      return {
        pageType: "product page",
        pageId: `product page: ${productId}`,
      };
    },
  },
  {
    match: (segments) => segments.includes("search"),
    resolve: () => ({
      pageType: "successful search",
      pageId: "successful search",
    }),
  },
  {
    match: (segments) => segments.includes("bag"),
    resolve: () => ({
      pageType: "shopping bag",
      pageId: "shopping bag",
    }),
  },
  {
    match: (segments) => segments.includes("wishlist"),
    resolve: () => ({
      pageType: "saved items",
      pageId: "saved items",
    }),
  },
];

const FLOOR_MAP: Record<string, string> = {
  topshop: "women",
  topman: "men",
};

export function resolvePageIdentity(
  pathname: string,
  searchParams: URLSearchParams,
): PageIdentity | null {
  const segments = pathname.split("/").filter(Boolean);

  for (const pattern of SEGMENT_PATTERNS) {
    if (pattern.match(segments)) {
      return pattern.resolve(segments, searchParams);
    }
  }

  const firstSegment = segments[0];
  if (segments.length === 1 && firstSegment && firstSegment in FLOOR_MAP) {
    const floor = FLOOR_MAP[firstSegment as keyof typeof FLOOR_MAP];
    return {
      pageType: "floor page",
      pageId: `floor page: ${floor}`,
    };
  }

  if (segments.length === 0) {
    return {
      pageType: "home page",
      pageId: "home page",
    };
  }

  return null;
}
